import React from 'react';
import { Box, Button, Card, CardContent, CardHeader, Flex, Heading, Switch, Text } from '@chatsdet/ui';
import type { TicketingCapabilities } from '../../../hooks/useTicketingProviders';

/** Order in which capabilities are displayed. */
const CAPABILITY_ORDER = [
  'createTicket',
  'webhookSync',
  'closeTicket',
  'searchTickets',
  'importTicket',
  'commentOnTicket',
  'tagTriggeredImport',
];

type IntegrationCapabilitiesProps = {
  capabilities: TicketingCapabilities;
  labels: Record<string, string>;
  enabledCapabilities: Record<string, boolean> | null;
  onSave: (prefs: Record<string, boolean>) => void;
  isSaving?: boolean;
};

export function IntegrationCapabilities({
  capabilities,
  labels,
  enabledCapabilities,
  onSave,
  isSaving,
}: IntegrationCapabilitiesProps) {
  const orderedKeys = CAPABILITY_ORDER.filter((key) => key in capabilities);

  const [localPrefs, setLocalPrefs] = React.useState<Record<string, boolean>>({});
  const [dirty, setDirty] = React.useState(false);

  // Sync from server state when enabledCapabilities changes
  React.useEffect(() => {
    setLocalPrefs(enabledCapabilities ?? {});
    setDirty(false);
  }, [enabledCapabilities]);

  function handleToggle(cap: string, isSelected: boolean) {
    setLocalPrefs((prev) => ({ ...prev, [cap]: isSelected }));
    setDirty(true);
  }

  function isEnabled(cap: string): boolean {
    if (!capabilities[cap]) return false;
    if (cap in localPrefs) return localPrefs[cap];
    return true;
  }

  return (
    <Card padding="lg">
      <CardHeader>
        <Heading level={3}>Feature Preferences</Heading>
        <Text size="sm" className="text-muted-foreground mt-1">
          Enable or disable capabilities for this integration
        </Text>
      </CardHeader>
      <CardContent>
        <Box className="space-y-3">
          {orderedKeys.map((cap) => {
            const supported = capabilities[cap];
            return (
              <Flex key={cap} align="center" justify="between" gap="sm">
                <Text
                  size="sm"
                  className={supported ? '' : 'text-muted-foreground/60'}
                >
                  {labels[cap] ?? cap}
                </Text>
                <Switch
                  size="sm"
                  aria-label={labels[cap] ?? cap}
                  isDisabled={!supported}
                  isSelected={isEnabled(cap)}
                  onChange={(selected) => handleToggle(cap, selected)}
                />
              </Flex>
            );
          })}
        </Box>
        <Flex gap="sm" className="mt-4">
          <Button
            variant="primary"
            size="sm"
            onPress={() => onSave(localPrefs)}
            isDisabled={!dirty || isSaving}
          >
            {isSaving ? 'Saving...' : 'Save Preferences'}
          </Button>
        </Flex>
      </CardContent>
    </Card>
  );
}
